/**
 * One shared WebSocket to /api/agent for the whole app (the Worker picks the user's agent from the session cookie).
 * Reconnects with jittered exponential backoff, ignores the Agents SDK protocol frames ("cf_agent_*"),
 * validates every frame with zod and dispatches `AgentEvent`s to subscribers.
 */
import { useEffect, useLayoutEffect, useRef, useSyncExternalStore } from "react";
import { z } from "zod";
import type { AgentEvent } from "../../shared/contracts";
import { api, wsOrigin } from "./api";

export type SocketStatus = "idle" | "connecting" | "open" | "closed";

const id = z.string().min(1);

// Nested payloads are validated on their essential fields and kept "loose" so new contract fields pass through.
const chatMessage = z.looseObject({
  id,
  direction: z.enum(["in", "out"]),
  channel: z.string(),
  kind: z.string(),
  text: z.string(),
  createdAt: z.number(),
});
const approval = z.looseObject({
  id,
  code: z.string(),
  title: z.string(),
  lines: z.array(z.looseObject({ label: z.string(), value: z.string() })),
  status: z.string(),
  createdAt: z.number(),
  expiresAt: z.number(),
});
const brief = z.looseObject({
  id,
  kind: z.string(),
  headline: z.string(),
  intro: z.string(),
  cards: z.array(z.looseObject({ title: z.string(), body: z.string(), category: z.string() })),
  createdAt: z.number(),
});
const mission = z.looseObject({ id, title: z.string(), goal: z.string(), status: z.string() });
const run = z.looseObject({ id, title: z.string(), status: z.string(), createdAt: z.number() });
const entry = z.looseObject({ kind: z.string(), at: z.number() });
const machine = z.looseObject({ id, kind: z.enum(["cloud", "local"]), name: z.string(), state: z.string() });
const settings = z.looseObject({
  agentName: z.string(),
  timezone: z.string(),
  mutedTopics: z.array(z.string()).catch([]),
  snoozeUntil: z.number().nullable().catch(null),
});

const agentEventSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("turn.started"), turnId: id, trigger: z.string() }),
  z.object({ type: z.literal("turn.delta"), turnId: id, text: z.string() }),
  z.object({ type: z.literal("turn.thinking"), turnId: id, text: z.string() }),
  z.object({ type: z.literal("turn.tool"), turnId: id, name: z.string(), status: z.string(), summary: z.string().catch("") }),
  z.object({ type: z.literal("turn.done"), turnId: id, status: z.string() }),
  z.object({ type: z.literal("chat.message"), message: chatMessage }),
  z.object({ type: z.literal("approval.upsert"), approval }),
  z.object({ type: z.literal("brief.created"), brief }),
  z.object({ type: z.literal("mission.upsert"), mission }),
  z.object({ type: z.literal("run.upsert"), run }),
  z.object({ type: z.literal("run.event"), runId: id, entry }),
  z.object({ type: z.literal("machine.upsert"), machine }),
  z.object({ type: z.literal("settings.updated"), settings }),
  z.object({ type: z.literal("rules.changed") }),
]);

/** Parses one text frame; null for protocol frames, unknown types and invalid payloads. */
export function parseAgentFrame(raw: unknown): AgentEvent | null {
  if (typeof raw !== "string" || raw.length === 0 || raw[0] !== "{") return null;
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof value !== "object" || value === null) return null;
  const type = (value as { type?: unknown }).type;
  if (typeof type !== "string" || type.startsWith("cf_agent")) return null;
  const parsed = agentEventSchema.safeParse(value);
  if (!parsed.success) {
    if (import.meta.env.DEV) console.warn("[pulsez] trame ignorée", type, parsed.error.issues);
    return null;
  }
  // The envelope and essential fields are checked; enums stay tolerant (unknown values render with fallbacks).
  return parsed.data as unknown as AgentEvent;
}

const MAX_BACKOFF_MS = 30_000;
const RECYCLE_AFTER_HIDDEN_MS = 120_000;

class AgentSocket {
  private ws: WebSocket | null = null;
  private wanted = false;
  private attempt = 0;
  private failuresBeforeOpen = 0;
  private everOpened = false;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private hiddenAt = 0;
  private statusValue: SocketStatus = "idle";
  /** Epoch of the last successful open (resources fetched before it may have missed events). */
  openedAt = 0;

  private readonly eventListeners = new Set<(e: AgentEvent) => void>();
  private readonly openListeners = new Set<(reconnect: boolean) => void>();
  private readonly statusListeners = new Set<() => void>();

  get status(): SocketStatus {
    return this.statusValue;
  }

  start(): void {
    if (this.wanted) return;
    this.wanted = true;
    window.addEventListener("online", this.onOnline);
    document.addEventListener("visibilitychange", this.onVisibility);
    this.connect();
  }

  stop(): void {
    if (!this.wanted && !this.ws) return;
    this.wanted = false;
    window.removeEventListener("online", this.onOnline);
    document.removeEventListener("visibilitychange", this.onVisibility);
    this.clearRetry();
    this.closeCurrent();
    this.everOpened = false;
    this.attempt = 0;
    this.failuresBeforeOpen = 0;
    this.setStatus("idle");
  }

  subscribe(fn: (e: AgentEvent) => void): () => void {
    this.eventListeners.add(fn);
    return () => this.eventListeners.delete(fn);
  }

  onOpen(fn: (reconnect: boolean) => void): () => void {
    this.openListeners.add(fn);
    return () => this.openListeners.delete(fn);
  }

  subscribeStatus = (fn: () => void): (() => void) => {
    this.statusListeners.add(fn);
    return () => this.statusListeners.delete(fn);
  };

  getStatus = (): SocketStatus => this.statusValue;

  /** Reconnects immediately (resets the backoff) unless a socket is open or opening. */
  reconnectNow(): void {
    if (!this.wanted) return;
    const ws = this.ws;
    if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;
    this.clearRetry();
    this.attempt = 0;
    this.connect();
  }

  private connect(): void {
    if (!this.wanted || this.ws) return;
    let ws: WebSocket;
    try {
      ws = new WebSocket(`${wsOrigin()}/api/agent`);
    } catch {
      this.scheduleRetry();
      return;
    }
    this.ws = ws;
    this.setStatus("connecting");
    let opened = false;

    ws.onopen = () => {
      if (this.ws !== ws) return;
      opened = true;
      const reconnect = this.everOpened;
      this.everOpened = true;
      this.attempt = 0;
      this.failuresBeforeOpen = 0;
      this.openedAt = Date.now();
      this.setStatus("open");
      for (const fn of [...this.openListeners]) fn(reconnect);
    };
    ws.onmessage = (ev: MessageEvent) => {
      if (this.ws !== ws) return;
      const event = parseAgentFrame(ev.data);
      if (!event) return;
      for (const fn of [...this.eventListeners]) {
        try {
          fn(event);
        } catch (err) {
          console.error("[pulsez] gestionnaire d'événement", err);
        }
      }
    };
    ws.onerror = () => {
      // A close event always follows; the retry is scheduled there.
    };
    ws.onclose = () => {
      if (this.ws !== ws) return;
      this.ws = null;
      if (!this.wanted) return;
      this.setStatus("closed");
      if (!opened) {
        this.failuresBeforeOpen += 1;
        // Browsers hide the upgrade status: after a few refusals, check the session (a 401 redirects to /connexion).
        if (this.failuresBeforeOpen % 3 === 0) void api("GET /api/me").catch(() => undefined);
      }
      this.scheduleRetry();
    };
  }

  private scheduleRetry(): void {
    if (!this.wanted || this.retryTimer) return;
    const base = Math.min(MAX_BACKOFF_MS, 1000 * 2 ** Math.min(this.attempt, 5));
    const delay = base / 2 + Math.random() * (base / 2);
    this.attempt += 1;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.connect();
    }, delay);
  }

  private clearRetry(): void {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
  }

  private closeCurrent(): void {
    const ws = this.ws;
    this.ws = null;
    if (!ws) return;
    ws.onopen = null;
    ws.onmessage = null;
    ws.onerror = null;
    ws.onclose = null;
    try {
      ws.close(1000);
    } catch {
      // already closed
    }
  }

  private setStatus(status: SocketStatus): void {
    if (this.statusValue === status) return;
    this.statusValue = status;
    for (const fn of [...this.statusListeners]) fn();
  }

  private readonly onOnline = (): void => this.reconnectNow();

  private readonly onVisibility = (): void => {
    if (document.visibilityState === "hidden") {
      this.hiddenAt = Date.now();
      return;
    }
    // Mobile browsers silently kill sockets in the background: recycle after a long absence.
    if (this.hiddenAt && Date.now() - this.hiddenAt > RECYCLE_AFTER_HIDDEN_MS && this.ws) {
      this.closeCurrent();
      this.setStatus("closed");
    }
    this.hiddenAt = 0;
    this.reconnectNow();
  };
}

export const agentSocket = new AgentSocket();

/** Subscribes a component to agent events. The latest `handler` is always used (no resubscription). */
export function useAgentEvents(handler: (event: AgentEvent) => void): void {
  const ref = useRef(handler);
  useLayoutEffect(() => {
    ref.current = handler;
  });
  useEffect(() => agentSocket.subscribe((e) => ref.current(e)), []);
}

/** Runs `handler` each time the socket opens (`reconnect` = it had been open before). */
export function useSocketOpen(handler: (reconnect: boolean) => void): void {
  const ref = useRef(handler);
  useLayoutEffect(() => {
    ref.current = handler;
  });
  useEffect(() => agentSocket.onOpen((r) => ref.current(r)), []);
}

export function useSocketStatus(): SocketStatus {
  return useSyncExternalStore(agentSocket.subscribeStatus, agentSocket.getStatus, agentSocket.getStatus);
}
